import { FAKE, inject, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createSignal } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { validate } from 'typia'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'
import { validationError } from 'components/discovery/shared/validation.js'
import { DEFAULT_PRODUCT_FEATURES, productPath } from 'components/discovery/pdp/shared/pdp-endpoint-paths.js'
import { GetPdpProductRequest, PDP_PRODUCT_API, type PdpProductAPI } from './pdp-product-api.js'

export const PDP_PRODUCT_STORE = Symbol('PDP_PRODUCT_STORE')

export type PdpProductSource = {
  productId?: number | null
}

@registerDependency(FAKE, PDP_PRODUCT_STORE)
@registerDependency(REAL, PDP_PRODUCT_STORE)
export class PdpProductStore {

  @inject(PDP_PRODUCT_API)
  private api!: PdpProductAPI

  product: () => PdpProduct | null
  private setProduct: (value: PdpProduct) => void

  private requested = new Map<string, Promise<void>>()

  constructor() {
    [this.product, this.setProduct] = createSignal<PdpProduct | null>(null)
  }

  resolveProductUrl = async ({ productId }: PdpProductSource): Promise<string> => {
    if (!productId) return ''

    const url = await createUrl(productPath(productId))
    DEFAULT_PRODUCT_FEATURES.forEach(feature => url.searchParams.append('features', feature))
    return url.href
  }

  loadProduct = async (source: PdpProductSource): Promise<void> => {
    const endpointUrl = await this.resolveProductUrl(source)
    if (!endpointUrl) return

    const pending = this.requested.get(endpointUrl)
    if (pending) return pending

    const request = this.fetchProduct(endpointUrl)
    this.requested.set(endpointUrl, request)
    return request
  }

  fetchProduct = async (endpointUrl: string): Promise<void> => {
    const result = await this.api.getProduct(new GetPdpProductRequest(endpointUrl))
    const productValidation = validate<PdpProduct>(result)

    if (!productValidation.success) {
      validationError('PdpProduct', productValidation.errors)
      return
    }

    this.setProduct(productValidation.data)
  }
}
